import React from 'react';
import styled from 'styled-components';

import { telemetry } from 'lib/telemetry';
import { logger } from 'lib/logger';

interface OwnProps {
  children: React.ReactNode;
  /**
   * Optional custom fallback. If omitted, a minimal themed message with
   * a retry button is rendered.
   */
  fallback?: React.ReactNode;
}

interface State {
  error: Error | null;
}

const FallbackContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.dangerSurface};
  border: 1px solid ${({ theme }) => theme.colors.dangerBorder};
  border-radius: ${({ theme }) => theme.radii.md};
  color: ${({ theme }) => theme.colors.dangerText};
  font-size: ${({ theme }) => theme.fontSizes.md};
  margin: ${({ theme }) => theme.spacing.xl};
`;

const FallbackHeading = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSizes.xl};
`;

const FallbackMessage = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
  font-family: monospace;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const RetryButton = styled.button`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textInverse};
  border: none;
  border-radius: ${({ theme }) => theme.radii.sm};
  font-size: ${({ theme }) => theme.fontSizes.md};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
  }
`;

/**
 * Catches any uncaught render-tree error, reports it to telemetry +
 * logger, and renders either the supplied `fallback` or a minimal
 * themed "Something went wrong" message with a retry button.
 *
 * Implemented as a classic class component because React error
 * boundaries still require `componentDidCatch` /
 * `getDerivedStateFromError` (functional boundaries need the
 * unapproved `react-error-boundary` package).
 */
class AppErrorBoundary extends React.Component<OwnProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    logger.error('Unhandled error in component tree', error);
    telemetry.trackException(error, { componentStack: info.componentStack ?? '' });
  }

  handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    const { fallback, children } = this.props;

    if (error) {
      if (fallback !== undefined) return fallback;

      return (
        <FallbackContainer role='alert'>
          <FallbackHeading>Something went wrong</FallbackHeading>
          <FallbackMessage>{error.message}</FallbackMessage>
          <RetryButton type='button' onClick={this.handleReset}>
            Try again
          </RetryButton>
        </FallbackContainer>
      );
    }

    return children;
  }
}

export default AppErrorBoundary;
